import type { Terrain, TerrainOp, TerrainStyle } from '@tanky/shared';

type RGB = [number, number, number];

interface Palette {
  top: RGB;
  top2: RGB;
  soil: RGB;
  deep: RGB;
  stripe: RGB;
}

const PALETTES: Record<TerrainStyle, Palette> = {
  hills: { top: [126, 200, 80], top2: [84, 156, 60], soil: [150, 101, 62], deep: [104, 68, 46], stripe: [120, 80, 52] },
  caves: { top: [176, 166, 190], top2: [132, 122, 150], soil: [104, 94, 122], deep: [70, 63, 86], stripe: [86, 77, 104] },
  moon: { top: [196, 196, 200], top2: [168, 168, 174], soil: [132, 132, 138], deep: [92, 92, 98], stripe: [112, 112, 118] },
  mars: { top: [222, 132, 80], top2: [196, 104, 60], soil: [168, 82, 46], deep: [112, 52, 32], stripe: [140, 64, 38] },
};

const SHADE_DEPTH = 40;

function noise(x: number, y: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) & 255) / 255;
}

/** Offscreen canvas s vykresleným terénem; po změně se překreslí jen dotčený obdélník. */
export interface SlideResult {
  moves: number[];
  /** Barva každého zrnka jako 0xRRGGBB. */
  colors: Uint32Array;
  bbox: [number, number, number, number];
}

export class TerrainGfx {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private img: ImageData;
  private pal: Palette;
  /** Počitadlo „ještě nedopadlých“ zrnek sesuvu na pixel (0 = viditelný). */
  private hidden: Uint16Array | null = null;
  /** Zvýší se při každém překreslení – podle něj 3D pohled pozná změnu terénu. */
  version = 0;

  constructor(
    private terrain: Terrain,
    style: TerrainStyle,
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = terrain.w;
    this.canvas.height = terrain.h;
    this.ctx = this.canvas.getContext('2d')!;
    this.img = this.ctx.createImageData(terrain.w, terrain.h);
    this.pal = PALETTES[style];
    this.render(0, 0, terrain.w - 1, terrain.h - 1);
  }

  setTerrain(terrain: Terrain) {
    this.terrain = terrain;
    this.hidden = null;
    this.ctx.clearRect(0, 0, terrain.w, terrain.h);
    this.img = this.ctx.createImageData(terrain.w, terrain.h);
    this.render(0, 0, terrain.w - 1, terrain.h - 1);
  }

  /**
   * Aplikuje op. U sesuvu vrátí přesuny zrnek i s jejich barvami; cílové pixely
   * zůstanou skryté, dokud se nezavolá reveal() (až zrnka doletí).
   */
  applyOp(op: TerrainOp): SlideResult | null {
    if (op[0] !== 2) {
      this.terrain.apply(op);
      const [, x, y, r] = op;
      this.render(x - r - 1, y - r - 1, x + r + 1, y + r + SHADE_DEPTH + 1);
      return null;
    }
    const moves = this.terrain.apply(op) ?? [];
    if (!moves.length) return null;
    const { w, h } = this.terrain;
    this.hidden ??= new Uint16Array(w * h);
    const px = this.img.data;
    const colors = new Uint32Array(moves.length / 4);
    let bx0 = Infinity;
    let by0 = Infinity;
    let bx1 = -Infinity;
    let by1 = -Infinity;
    for (let i = 0; i < moves.length; i += 4) {
      const [fx, fy, tx, ty] = [moves[i], moves[i + 1], moves[i + 2], moves[i + 3]];
      // Barvu bereme z dosud vykresleného obrázku (zdroj ještě „vidíme“)
      const k = (fy * w + fx) * 4;
      colors[i / 4] = px[k + 3] ? (px[k] << 16) | (px[k + 1] << 8) | px[k + 2] : 0x8a5a3b;
      this.hidden[ty * w + tx]++;
      bx0 = Math.min(bx0, fx, tx);
      bx1 = Math.max(bx1, fx, tx);
      by0 = Math.min(by0, fy, ty);
      by1 = Math.max(by1, fy, ty);
    }
    const bbox: [number, number, number, number] = [bx0 - 1, by0 - 1, bx1 + 1, Math.min(h - 1, by1 + SHADE_DEPTH + 1)];
    this.render(...bbox);
    return { moves, colors, bbox };
  }

  /** Zrnka dopadla – odkrýt jejich cílové pixely. */
  reveal(slide: SlideResult) {
    if (!this.hidden) return;
    const { w } = this.terrain;
    const { moves } = slide;
    for (let i = 0; i < moves.length; i += 4) {
      const k = moves[i + 3] * w + moves[i + 2];
      if (this.hidden[k]) this.hidden[k]--;
    }
    this.render(...slide.bbox);
  }

  private render(x0: number, y0: number, x1: number, y1: number) {
    const { w, h, data: terrainData } = this.terrain;
    const hidden = this.hidden;
    // Skryté (ještě padající) pixely kreslíme jako prázdné
    const solidAt = hidden ? (i: number) => (hidden[i] ? 0 : terrainData[i]) : (i: number) => terrainData[i];
    x0 = Math.max(0, Math.floor(x0));
    y0 = Math.max(0, Math.floor(y0));
    x1 = Math.min(w - 1, Math.ceil(x1));
    y1 = Math.min(h - 1, Math.ceil(y1));
    if (x1 < x0 || y1 < y0) return;
    const px = this.img.data;
    const p = this.pal;
    for (let x = x0; x <= x1; x++) {
      // Kolik pevných pixelů je přímo nad y0
      let run = 0;
      for (let y = y0 - 1; y >= 0 && run < SHADE_DEPTH; y--) {
        if (solidAt(y * w + x)) run++;
        else break;
      }
      for (let y = y0; y <= y1; y++) {
        const i = (y * w + x) * 4;
        if (!solidAt(y * w + x)) {
          px[i + 3] = 0;
          run = 0;
          continue;
        }
        const n = noise(x, y);
        let c: RGB;
        if (run < 3) c = p.top;
        else if (run < 6) c = p.top2;
        else if (run < SHADE_DEPTH) {
          const k = (run - 6) / (SHADE_DEPTH - 6);
          c = [
            p.soil[0] + (p.deep[0] - p.soil[0]) * k,
            p.soil[1] + (p.deep[1] - p.soil[1]) * k,
            p.soil[2] + (p.deep[2] - p.soil[2]) * k,
          ];
        } else {
          // Vrstvy ve hloubce
          c = ((y + Math.floor(noise(x >> 4, 7) * 12)) >> 3) % 5 === 0 ? p.stripe : p.deep;
        }
        const v = (n - 0.5) * 18 + (n > 0.93 ? 14 : 0);
        px[i] = Math.max(0, Math.min(255, c[0] + v));
        px[i + 1] = Math.max(0, Math.min(255, c[1] + v));
        px[i + 2] = Math.max(0, Math.min(255, c[2] + v));
        px[i + 3] = 255;
        if (run < SHADE_DEPTH) run++;
      }
    }
    this.ctx.putImageData(this.img, 0, 0, x0, y0, x1 - x0 + 1, y1 - y0 + 1);
    this.version++;
  }

  /** Vykreslená barva pixelu jako 0xRRGGBB, nebo -1 u prázdného (i ještě padajícího) pixelu. */
  rgbAt(x: number, y: number): number {
    const k = (y * this.terrain.w + x) * 4;
    const px = this.img.data;
    return px[k + 3] ? (px[k] << 16) | (px[k + 1] << 8) | px[k + 2] : -1;
  }
}
